import { UntilDestroy } from '@ngneat/until-destroy'
import { setupSocketAuth } from './socket-auth'
import { Injectable, inject } from '@angular/core'
import { Subject, BehaviorSubject } from 'rxjs'
import { Socket } from 'socket.io-client'
import { PersonaData } from './persona/persona.mapping'
import { environment } from '../../environments/environment'
import { FirebaseTokenProvider } from './roleplay/active-session-count.service'
import { SOCKET_IO_FACTORY } from './socket-io.factory'

/**
 * Firebase ID トークン取得の再試行間隔（ms）。ネットワーク瞬断や再接続直後の
 * 一過性の取得失敗を吸収する（初回 + 2 回リトライ）
 */
const AUTH_TOKEN_RETRY_DELAYS_MS = [250, 750]

/**
 * トークン取得試行の全体期限（ms）。低遅延ロープレ（streaming-socket.service.ts）の
 * サーバー側再接続猶予（roleplay-ws/src/live/config.ts の reconnectGraceMs、既定 10 秒）を
 * 認証だけで使い切らないよう、同じ上限を設ける
 */
const AUTH_TOKEN_DEADLINE_MS = 4000

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export interface TranscriptionResult {
  sessionId: string
  transcription: string
  timestamp: string
  userId: string
  confidence?: number
  originalAudioConfidence?: number
  alternatives?: string[]
}

export interface AIResponse {
  sessionId: string
  assessment?: 'Bad' | 'Normal' | 'Good' | 'Excellent'
  response: string
  audioData: string
  timestamp: string
  isFinished?: boolean
  speaker?: string
  score?: number
  reason?: string
  reasonDetail?: string
  metadata?: {
    inputConfidence?: number
    originalAudioConfidence?: number
    processingTime?: number
    source?: string
  }
}

export interface AudioQualityReport {
  sessionId: string
  averageVolume: number
  peakVolume: number
  noiseLevel: number
  timestamp: string
}

export interface ConnectionStatus {
  connected: boolean
  reconnecting: boolean
  error?: string
}

export interface AssessmentCreatedEvent {
  sessionId: string
  assessmentId: string
}

export interface AssessmentCreationFailedEvent {
  sessionId: string
  error: string
}

export interface BridgeAudioEvent {
  sessionId: string
  audioData: string
  fillerId: string
  duration: number
  timestamp: string
}

export interface SessionEndedEvent {
  sessionId: string
  assessmentId?: string
}

// Cloud STT Streaming Events
export interface UserTurnStartedEvent {
  sessionId: string
  maxDurationMs: number
  timestamp: string
}

export interface InterimTranscriptEvent {
  sessionId: string
  text: string
  accumulatedText: string
  timestamp: string
}

export interface UserTurnCompleteEvent {
  sessionId: string
  fullText: string
  timestamp: string
}

export interface AiTurnCompleteEvent {
  sessionId: string
  timestamp: string
  skipped?: boolean
  reason?: string
}

export interface SttErrorEvent {
  sessionId: string
  error: string
  canRetry: boolean
}

export interface RecordingCompleteEvent {
  sessionId: string
  urls: {
    userAudioUrl: string
    aiAudioUrl: string
    combinedAudioUrl: string
    metadataUrl: string
  }
  stats: {
    totalTurns: number
    userSpeakingTimeMs: number
    aiSpeakingTimeMs: number
  }
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  static debugCounter = 0
  static instanceCounter = 0
  private instanceId: number
  private socket: Socket | null = null
  private authInvalidated = false
  private readonly socketAuth = setupSocketAuth(
    this,
    () => this.socket,
    () => {
      this.authInvalidated = true
      this.disconnect()
    },
  )
  private reconnectAttempts = 0
  // socket.io 自動再接続（10回）が全て失敗した後の手動再接続の上限
  private maxReconnectAttempts = 3
  // auth コールバック呼び出しごと・切断ごとに増分し、古い呼び出しの結果を捨てるための番号
  private authAttemptSeq = 0
  private readonly socketFactory = inject(SOCKET_IO_FACTORY)
  private readonly tokenProvider = inject(FirebaseTokenProvider)

  private transcriptionSubject = new Subject<TranscriptionResult>()
  private aiResponseSubject = new Subject<AIResponse>()
  private aiProcessingSubject = new BehaviorSubject<boolean>(false)
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>({
    connected: false,
    reconnecting: false,
  })

  private transcriptionWarningSubject = new Subject<{
    sessionId: string
    warning: string
    confidence: number
  }>()
  private audioQualitySubject = new Subject<AudioQualityReport>()
  private sessionStatusSubject = new Subject<{
    sessionId: string
    status: string
    message?: string
    selectedScenario?: { id: string; name: string }
    audioData?: string
    audioDuration?: number
  }>()
  private assessmentCreatedSubject = new Subject<AssessmentCreatedEvent>()
  private assessmentCreationFailedSubject =
    new Subject<AssessmentCreationFailedEvent>()
  private bridgeAudioSubject = new Subject<BridgeAudioEvent>()
  private sessionEndedSubject = new Subject<SessionEndedEvent>()
  public sessionEnded$ = this.sessionEndedSubject.asObservable()

  // Cloud STT Streaming Subjects
  private userTurnStartedSubject = new Subject<UserTurnStartedEvent>()
  private interimTranscriptSubject = new Subject<InterimTranscriptEvent>()
  private userTurnCompleteSubject = new Subject<UserTurnCompleteEvent>()
  private aiTurnCompleteSubject = new Subject<AiTurnCompleteEvent>()
  private sttErrorSubject = new Subject<SttErrorEvent>()
  private recordingCompleteSubject = new Subject<RecordingCompleteEvent>()

  public transcription$ = this.transcriptionSubject.asObservable()
  public aiResponse$ = this.aiResponseSubject.asObservable()
  public aiProcessing$ = this.aiProcessingSubject.asObservable()
  public connectionStatus$ = this.connectionStatusSubject.asObservable()
  public transcriptionWarning$ = this.transcriptionWarningSubject.asObservable()
  public audioQuality$ = this.audioQualitySubject.asObservable()
  public sessionStatus$ = this.sessionStatusSubject.asObservable()
  public assessmentCreated$ = this.assessmentCreatedSubject.asObservable()
  public assessmentCreationFailed$ =
    this.assessmentCreationFailedSubject.asObservable()
  public bridgeAudio$ = this.bridgeAudioSubject.asObservable()

  // Cloud STT Streaming Observables
  public userTurnStarted$ = this.userTurnStartedSubject.asObservable()
  public interimTranscript$ = this.interimTranscriptSubject.asObservable()
  public userTurnComplete$ = this.userTurnCompleteSubject.asObservable()
  public aiTurnComplete$ = this.aiTurnCompleteSubject.asObservable()
  public sttError$ = this.sttErrorSubject.asObservable()
  public recordingComplete$ = this.recordingCompleteSubject.asObservable()

  constructor() {
    this.instanceId = ++WebSocketService.instanceCounter
  }

  async connect(): Promise<void> {
    if (this.authInvalidated) throw new Error('Session invalidated')
    if (this.socket?.connected) {
      return
    }

    this.socketAuth.stop()

    // 切断済みの旧ソケットが残っていればクリーンアップ
    if (this.socket) {
      this.socket.io.removeAllListeners()
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    this.connectionStatusSubject.next({ connected: false, reconnecting: true })

    return new Promise((resolve, reject) => {
      let settled = false
      let handshakeToken: string | undefined
      const baseUrl = environment.roleplayWsUrl
      const socketPath = '/roleplay/ws/socket.io/'

      console.log('🔌 Attempting WebSocket connection:', {
        baseUrl,
        socketPath,
      })

      // 初回接続中の失敗（タイムアウト or 認証全滅）を、タイムアウト満了時と
      // 同じ結果（状態通知・ソケット破棄・connect() の reject）に一本化する
      const failInitialConnection = (message: string) => {
        if (settled || this.socket?.connected) return
        settled = true
        clearTimeout(connectionTimeout)
        console.error(`❌ ${message}`)
        this.connectionStatusSubject.next({
          connected: false,
          reconnecting: false,
          error: message,
        })
        if (this.socket) {
          this.socket.io.removeAllListeners()
          this.socket.removeAllListeners()
          this.socket.disconnect()
          this.socket = null
        }
        reject(new Error(message))
      }

      const socket = this.socketFactory(baseUrl, {
        path: socketPath,
        // auth を関数形式で渡すことで、初回接続だけでなく自動再接続のたびに
        // 最新の Firebase ID トークンを取得する。静的オブジェクトだと初回取得した
        // トークンを使い回し、約1時間で失効して再接続が "Token has expired" で
        // 失敗する（SBL-604）。
        auth: (cb: (data: { token: string }) => void) => {
          const attemptId = ++this.authAttemptSeq
          this.getAuthTokenWithRetry()
            .then((token) => {
              // 古い auth 呼び出しの結果は無視する。接続済み transport に古い
              // トークンで CONNECT を二重送信すると、サーバーが invalid state
              // として接続ごと閉じるため。破棄・差し替え済みソケットの結果も同様に捨てる
              if (attemptId !== this.authAttemptSeq || this.socket !== socket)
                return
              handshakeToken = token
              cb({ token })
            })
            .catch((error) => {
              if (attemptId !== this.authAttemptSeq || this.socket !== socket)
                return
              console.error(
                '❌ Firebase トークン取得に失敗しました（リトライ後も取得不可）:',
                error,
              )
              if (!settled) {
                failInitialConnection('Firebase auth token unavailable')
                return
              }
              // セッション確立後の再接続失敗は reconnect_failed と同じ経路へ流す
              this.handleReconnection()
            })
        },
        timeout: 20000,
        forceNew: true,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 10,
        upgrade: true,
        rememberUpgrade: true,
        withCredentials: false,
      })
      this.socket = socket

      const connectionTimeout = setTimeout(() => {
        failInitialConnection('Connection timeout after 20s')
      }, 20000)

      this.socket.on('connect', () => {
        this.socketAuth.connected(handshakeToken)
        clearTimeout(connectionTimeout)
        console.log('✅ Connected to WebSocket server successfully!', {
          socketId: this.socket?.id,
          transport: this.socket?.io?.engine?.transport?.name,
        })
        this.reconnectAttempts = 0
        this.connectionStatusSubject.next({
          connected: true,
          reconnecting: false,
        })
        this.setupEventListeners()
        if (!settled) {
          settled = true
          resolve()
        }
      })

      this.socket.on('connect_error', (error) => {
        clearTimeout(connectionTimeout)
        console.error('❌ WebSocket connection error:', {
          message: error.message,
        })

        const isAuthError =
          error.message.toLowerCase().includes('auth') ||
          error.message.toLowerCase().includes('unauthorized') ||
          error.message.toLowerCase().includes('forbidden')

        this.connectionStatusSubject.next({
          connected: false,
          reconnecting: !isAuthError,
          error: error.message,
        })

        if (isAuthError) {
          console.error('❌ Authentication error - not retrying automatically')
        }
        // 非認証エラーの場合は socket.io Manager が自動再接続を試みる
        // 手動再接続は reconnect_failed でのみ行う
        if (!settled) {
          settled = true
          reject(error)
        }
      })

      this.socket.on('auth:invalidated', this.socketAuth.invalidated)

      this.socket.on('disconnect', (reason) => {
        this.socketAuth.stop()
        // 切断された transport の auth 結果が遅れて返っても、再接続と重ならないよう捨てる
        this.authAttemptSeq++
        clearTimeout(connectionTimeout)
        console.log('🔌 WebSocket disconnected:', reason)

        if (reason === 'io server disconnect') {
          // サーバーが明示的に切断 → socket.io は自動再接続を行わない
          // (reconnect_attempt / reconnect_failed は発火しない) ため手動で再接続
          this.connectionStatusSubject.next({
            connected: false,
            reconnecting: true,
          })
          this.handleReconnection()
        } else if (reason === 'io client disconnect') {
          // クライアントが意図的に切断 → 再接続不要
          this.connectionStatusSubject.next({
            connected: false,
            reconnecting: false,
          })
        } else {
          // 'transport close', 'ping timeout', 'transport error' 等
          // socket.io が自動再接続する
          this.connectionStatusSubject.next({
            connected: false,
            reconnecting: true,
          })
        }
      })

      // Manager レベルの再接続イベント
      this.socket.io.on('reconnect_attempt', (attempt) => {
        console.log(`🔄 WebSocket reconnection attempt ${attempt}`)
        this.connectionStatusSubject.next({
          connected: false,
          reconnecting: true,
        })
      })

      this.socket.io.on('reconnect', (attempt) => {
        console.log(`✅ WebSocket reconnected after ${attempt} attempt(s)`)
      })

      this.socket.io.on('reconnect_error', (error) => {
        console.error('❌ WebSocket reconnection error:', error.message)
      })

      this.socket.io.on('reconnect_failed', () => {
        console.error(
          '❌ WebSocket auto-reconnection failed, falling back to manual reconnection',
        )
        this.handleReconnection()
      })
    })
  }

  /**
   * Firebase ID トークンを取得する。
   * getIdToken() はキャッシュが有効ならそれを返し、失効（間近）の場合のみ
   * 自動でリフレッシュするため、Socket.io の auth コールバックから（再）接続の
   * たびに呼び出すことで常に有効なトークンを渡せる。強制リフレッシュ（引数 true）
   * にしないことで、ネットワーク瞬断による再接続時に Firebase への往復を避ける。
   */
  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getIdToken()
    console.log('🔐 Firebase auth token obtained for WebSocket')
    return token
  }

  /**
   * Firebase ID トークンを逐次リトライ付きで取得する（初回 + 2 回、待ち
   * 250ms/750ms）。全体期限 AUTH_TOKEN_DEADLINE_MS は Promise.race で切るが、
   * Firebase 内部の fetch 自体は止まらない。期限後に遅れて返る結果は
   * auth コールバック側の番号チェック（authAttemptSeq）で捨てる。
   */
  private async getAuthTokenWithRetry(): Promise<string> {
    const attempts = async (): Promise<string> => {
      let lastError: unknown
      for (let i = 0; i <= AUTH_TOKEN_RETRY_DELAYS_MS.length; i++) {
        try {
          return await this.getAuthToken()
        } catch (error) {
          lastError = error
          if (i < AUTH_TOKEN_RETRY_DELAYS_MS.length) {
            await delay(AUTH_TOKEN_RETRY_DELAYS_MS[i])
          }
        }
      }
      throw lastError
    }

    return Promise.race([
      attempts(),
      delay(AUTH_TOKEN_DEADLINE_MS).then((): never => {
        throw new Error(
          `Firebase トークン取得が ${AUTH_TOKEN_DEADLINE_MS}ms 以内に完了しませんでした`,
        )
      }),
    ])
  }

  private handleReconnection(): void {
    if (this.authInvalidated) return
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      this.connectionStatusSubject.next({
        connected: false,
        reconnecting: true,
      })

      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000)
      console.log(
        `Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`,
      )

      setTimeout(() => {
        if (this.authInvalidated) return
        this.connect().catch((error) => {
          console.error('Reconnection failed:', error)
        })
      }, delay)
    } else {
      console.error('Max reconnection attempts reached')
      this.connectionStatusSubject.next({
        connected: false,
        reconnecting: false,
        error: 'Max reconnection attempts reached',
      })
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return

    // メッセージハンドラのみ個別削除（connect/disconnect/connect_error は保持）
    const messageEvents = [
      'transcription-result',
      'ai-response',
      'ai-processing-started',
      'bridge-audio',
      'ai-error',
      'transcription-error',
      'session-error',
      'transcription-warning',
      'audio-buffered',
      'assessment-created',
      'assessment-creation-failed',
      'session-started',
      'session-ended',
      'participant-audio-level',
      'user-turn-started',
      'interim-transcript',
      'user-turn-complete',
      'ai-turn-complete',
      'stt-error',
      'recording-complete',
      'recording-error',
    ]
    for (const event of messageEvents) {
      this.socket.removeAllListeners(event)
    }
    this.socket.offAny()

    // 全てのWebSocketメッセージを生ログ出力
    this.socket.onAny((eventName, ...args) => {
      console.log('[WebSocket RAW]', eventName, args)
    })

    this.socket.on('transcription-result', (data: TranscriptionResult) => {
      console.log('Transcription result received:', {
        text: data.transcription.substring(0, 50),
        confidence: data.confidence,
        originalConfidence: data.originalAudioConfidence,
      })
      this.transcriptionSubject.next(data)
    })

    // Track last AI response to prevent server-side duplicates
    let lastAiResponse = ''
    let lastAiTimestamp = 0
    let aiResponseCount = 0

    this.socket.on('ai-response', (data: AIResponse) => {
      console.log('AI response received:', {
        text: data.response.substring(0, 50),
        hasAudio: !!data.audioData,
        metadata: data.metadata,
      })

      // WORKAROUND: Filter duplicate events from server (server sends same event 3x)
      // TODO: Fix server-side bug in ../roleplay-ws
      const now = Date.now()
      const isDuplicate =
        data.response === lastAiResponse && now - lastAiTimestamp < 500

      if (isDuplicate) {
        aiResponseCount++
        console.warn(
          `⚠️ WEBSOCKET: Duplicate ai-response #${aiResponseCount} detected and filtered (server-side bug)`,
        )
        return
      }

      // Reset counter and update tracking
      aiResponseCount = 0
      lastAiResponse = data.response
      lastAiTimestamp = now
      this.aiProcessingSubject.next(false)
      this.aiResponseSubject.next(data)
    })

    this.socket.on(
      'ai-processing-started',
      (data: { sessionId: string; transcriptionConfidence?: number }) => {
        console.log('AI processing started:', data)
        this.aiProcessingSubject.next(true)
      },
    )

    this.socket.on('bridge-audio', (data: BridgeAudioEvent) => {
      console.log('Bridge audio received:', {
        fillerId: data.fillerId,
        duration: data.duration,
        dataSize: data.audioData.length,
      })

      if (!data.audioData || data.audioData.length === 0) {
        console.error('[WebSocket] Bridge audio data is empty')
      }

      console.log('[Websocket] emitting bridge audio to subscribers')

      this.bridgeAudioSubject.next(data)
    })

    this.socket.on(
      'ai-error',
      (error: {
        sessionId: string
        error: string
        canRetry?: boolean
        audioData?: string
        audioDuration?: number
      }) => {
        console.error('AI processing error:', error)
        this.aiProcessingSubject.next(false)
        this.sessionStatusSubject.next({
          sessionId: error.sessionId,
          status: 'ai_error',
          message: error.error,
          audioData: error.audioData,
          audioDuration: error.audioDuration,
        })
      },
    )

    this.socket.on(
      'transcription-error',
      (error: { sessionId: string; error: string; canRetry?: boolean }) => {
        console.error('Transcription error:', error)
        this.sessionStatusSubject.next({
          sessionId: error.sessionId,
          status: 'transcription_error',
          message: error.error,
        })
      },
    )

    this.socket.on(
      'session-error',
      (error: { sessionId: string; error: string }) => {
        console.error('Session error:', error)
        this.sessionStatusSubject.next({
          sessionId: error.sessionId,
          status: 'session_error',
          message: error.error,
        })
      },
    )

    this.socket.on(
      'transcription-warning',
      (data: { sessionId: string; warning: string; confidence: number }) => {
        console.warn('Transcription warning:', data)
        this.transcriptionWarningSubject.next(data)
      },
    )

    this.socket.on(
      'audio-buffered',
      (data: { sessionId: string; sequence: number }) => {
        console.log('Audio buffered:', data)
      },
    )

    this.socket.on('assessment-created', (data: AssessmentCreatedEvent) => {
      console.log('Assessment created:', data)
      this.assessmentCreatedSubject.next(data)
    })

    this.socket.on(
      'assessment-creation-failed',
      (data: AssessmentCreationFailedEvent) => {
        console.error('Assessment creation failed:', data)
        this.assessmentCreationFailedSubject.next(data)
      },
    )

    this.socket.on(
      'session-started',
      (data: {
        sessionId: string
        status: string
        selectedScenario?: { id: string; name: string }
      }) => {
        console.log('Session started:', data)
        this.sessionStatusSubject.next({
          sessionId: data.sessionId,
          status: 'started',
          selectedScenario: data.selectedScenario,
        })
      },
    )

    this.socket.on('session-ended', (data: SessionEndedEvent) => {
      console.log('Session ended:', data)
      this.sessionEndedSubject.next(data)
    })

    this.socket.on(
      'participant-audio-level',
      (data: { userId: string; audioLevel: number; timestamp: string }) => {
        console.log('Participant audio level:', data)
      },
    )

    // Cloud STT Streaming Events
    this.socket.on('user-turn-started', (data: UserTurnStartedEvent) => {
      console.log('🎤 ユーザーターン開始')
      this.userTurnStartedSubject.next(data)
    })

    // Track last events to prevent server-side duplicates
    let lastInterimTranscript = ''
    let lastInterimTimestamp = 0
    let interimTranscriptCount = 0

    this.socket.on('interim-transcript', (data: InterimTranscriptEvent) => {
      // WORKAROUND: Filter duplicate events from server (server sends same event 3x)
      // TODO: Fix server-side bug in ../roleplay-ws
      const now = Date.now()
      const isDuplicate =
        data.accumulatedText === lastInterimTranscript &&
        now - lastInterimTimestamp < 500

      if (isDuplicate) {
        interimTranscriptCount++
        console.warn(
          `[No.1-FILTERED] ⚠️ WEBSOCKET: Duplicate #${interimTranscriptCount} detected and filtered (server-side bug)`,
        )
        return
      }

      // Reset counter and update tracking
      interimTranscriptCount = 0
      lastInterimTranscript = data.accumulatedText
      lastInterimTimestamp = now

      this.interimTranscriptSubject.next(data)
    })

    // Track last user-turn-complete to prevent server-side duplicates
    let lastUserTurnText = ''
    let lastUserTurnTimestamp = 0
    let userTurnCompleteCount = 0

    this.socket.on('user-turn-complete', (data: UserTurnCompleteEvent) => {
      console.log('✅ ユーザー発話完了:', data.fullText)

      // WORKAROUND: Filter duplicate events from server (server sends same event 3x)
      // TODO: Fix server-side bug in ../roleplay-ws
      const now = Date.now()
      const isDuplicate =
        data.fullText === lastUserTurnText && now - lastUserTurnTimestamp < 500

      if (isDuplicate) {
        userTurnCompleteCount++
        console.warn(
          `⚠️ WEBSOCKET: Duplicate user-turn-complete #${userTurnCompleteCount} detected and filtered (server-side bug)`,
        )
        return
      }

      // Reset counter and update tracking
      userTurnCompleteCount = 0
      lastUserTurnText = data.fullText
      lastUserTurnTimestamp = now

      this.userTurnCompleteSubject.next(data)
    })

    // Track last ai-turn-complete to prevent server-side duplicates
    let lastAiTurnSessionId = ''
    let lastAiTurnTimestamp = 0
    let aiTurnCompleteCount = 0

    this.socket.on('ai-turn-complete', (data: AiTurnCompleteEvent) => {
      console.log('AI turn complete:', data)

      // WORKAROUND: Filter duplicate events from server (server sends same event 3x)
      // TODO: Fix server-side bug in ../roleplay-ws
      const now = Date.now()
      const isDuplicate =
        data.sessionId === lastAiTurnSessionId &&
        now - lastAiTurnTimestamp < 500

      if (isDuplicate) {
        aiTurnCompleteCount++
        console.warn(
          `⚠️ WEBSOCKET: Duplicate ai-turn-complete #${aiTurnCompleteCount} detected and filtered (server-side bug)`,
        )
        return
      }

      // Reset counter and update tracking
      aiTurnCompleteCount = 0
      lastAiTurnSessionId = data.sessionId
      lastAiTurnTimestamp = now

      this.aiTurnCompleteSubject.next(data)
    })

    this.socket.on('stt-error', (data: SttErrorEvent) => {
      console.error('STT error:', data)
      this.sttErrorSubject.next(data)
    })

    this.socket.on('recording-complete', (data: RecordingCompleteEvent) => {
      console.log('Recording complete:', data)
      this.recordingCompleteSubject.next(data)
    })

    this.socket.on(
      'recording-error',
      (data: { sessionId: string; error: string }) => {
        console.error('Recording error:', data)
      },
    )
  }

  startRoleplaySession(
    sessionId: string,
    personaData: PersonaData,
    skillMapId?: string,
    phaseIds?: string[],
    sessionTitle?: string,
  ): void {
    if (!this.socket?.connected) {
      throw new Error('WebSocket not connected')
    }
    this.socket.emit('start-roleplay-session', {
      sessionId,
      personaData,
      skillMapId,
      phaseIds,
      sessionTitle,
    })
  }

  sendTextMessage(
    sessionId: string,
    text: string,
    personaData: PersonaData,
    confidence?: number,
    audioData?: string,
  ): void {
    if (!this.socket?.connected) {
      throw new Error('WebSocket not connected')
    }

    const payload: {
      sessionId: string
      text: string
      personaData: PersonaData
      source: string
      confidence: number
      timestamp: string
      audioData?: string
    } = {
      sessionId,
      text,
      personaData,
      source: 'speech-recognition',
      confidence: confidence || 1.0,
      timestamp: new Date().toISOString(),
    }

    if (audioData) {
      payload.audioData = audioData
    }

    console.log('📤 Sending text message:', {
      sessionId,
      text: text.substring(0, 50),
      source: payload.source,
      confidence: payload.confidence,
      hasAudio: !!audioData,
    })

    this.socket.emit('text-message', payload)
  }

  sendAudioData(
    sessionId: string,
    audioData: string,
    isComplete: boolean,
    sequence: number,
    personaData: PersonaData,
    metadata?: {
      confidence?: number
      duration?: number
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      qualityMetrics?: any
    },
  ): void {
    if (!this.socket?.connected) {
      throw new Error('WebSocket not connected')
    }

    const payload = {
      sessionId,
      audioData,
      isComplete,
      sequence,
      personaData,
      ...metadata,
    }

    console.log('Sending audio data:', {
      sessionId,
      isComplete,
      sequence,
      dataSize: audioData.length,
      confidence: metadata?.confidence,
      duration: metadata?.duration,
    })

    this.socket.emit('audio-data', payload)
  }

  endRoleplaySession(
    sessionId: string,
    createAssessment: boolean = true,
  ): void {
    if (!this.socket?.connected) {
      throw new Error('WebSocket not connected')
    }

    console.log('Ending roleplay session:', sessionId, createAssessment)
    this.socket.emit('end-roleplay-session', { sessionId, createAssessment })
  }

  disconnect(): void {
    this.socketAuth.stop()
    console.log('Disconnecting WebSocket...')

    if (this.socket) {
      this.socket.io.removeAllListeners()
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    this.connectionStatusSubject.next({
      connected: false,
      reconnecting: false,
    })

    this.aiProcessingSubject.next(false)
    this.reconnectAttempts = 0

    console.log('WebSocket disconnected')
  }

  isConnected(): boolean {
    return this.socket?.connected || false
  }

  sendDeviceInfo(
    sessionId: string,
    deviceInfo: {
      platform: string
      userAgent: string
      selectedMicId: string | null
      selectedMicLabel: string | null
      selectedSpeakerId: string | null
      selectedSpeakerLabel: string | null
      isExternalMic: boolean
    },
  ): void {
    if (!this.socket?.connected) {
      console.warn('Cannot send device info: WebSocket not connected')
      return
    }

    this.socket.emit('device-info', {
      sessionId,
      ...deviceInfo,
      timestamp: new Date().toISOString(),
    })
  }

  /**
   * Start a user turn (begin listening for audio)
   */
  startUserTurn(sessionId: string, personaData: PersonaData): void {
    console.log(
      `[START-TURN] 🎤 startUserTurn() called for session ${sessionId}`,
    )
    if (!this.socket?.connected) {
      throw new Error('WebSocket not connected')
    }

    console.log(`[START-TURN] 🎤 Emitting start-user-turn to server`)
    this.socket.emit('start-user-turn', { sessionId, personaData })
  }

  /**
   * Send audio stream data (PCM chunks)
   */
  sendAudioStream(
    sessionId: string,
    audioData: ArrayBuffer,
    sequence: number,
  ): void {
    if (!this.socket?.connected) {
      return
    }

    // Send as binary (ArrayBuffer)
    this.socket.emit('audio-stream', {
      sessionId,
      audioData,
      sequence,
    })
  }
}
